import { observable, action, runInAction } from "mobx";
import agent from "../agent";
import profileStore from "./profileStore";
import venueStore from "./venueStore";

import _ from "lodash";
import { Promise } from "bluebird";

import { ProfileType, ProfileRelationType } from "../types/enum";
import { emptyRosterFilters } from "../helper/actRosterQuery";

class VenueActStore {
    @observable staffRosterCriteria = { ...emptyRosterFilters(), search: "" };
    @observable staffRosterLoading = false;
    @observable staffRosterError = "";
    @observable staffRosterVenues = [];
    @observable staffVenueOptionsError = "";
    @observable staffVenueOptionsLoading = false;
    @observable staffRosterVenueId = "all";
    @observable staffPagination = { currentPage: 1, pageSize: 25, isMore: false, allLoaded: false, loadingAll: false };
    staffPageCache = {};
    staffRosterRequest = null;
    staffVenuesReady = false;
    staffVenuesPromise = null;
    staffVenueLookupVersion = 0;
    staffRetry = { page: 1, all: false };
    staffRosterVersion = 0;
    staffRosterActive = false;
    staffRosterActor = null;
    staffRosterSession = 0;
    staffPendingRosterVersion = 0;

    @action
    startStaffRosterQuery() {
        this.staffRosterSession += 1;
        this.staffRosterActive = true;
        this.staffRosterActor = null;
        this.staffRosterCriteria = { ...emptyRosterFilters(), search: "" };
        this.staffRosterVenues = [];
        this.staffRosterVenueId = "all";
        this.staffPagination = { currentPage: 1, pageSize: 25, isMore: false, allLoaded: false, loadingAll: false };
        this.staffVenuesReady = false;
        this.staffVenuesPromise = null;
        this.staffVenueOptionsError = "";
        this.staffVenueOptionsLoading = false;
        this.staffPageCache = {};
        this.staffRosterRequest = null;
        this.staffRosterVersion += 1;
        this.staffRosterError = "";
    }

    @action
    stopStaffRosterQuery() {
        this.staffRosterSession += 1;
        this.staffRosterActive = false;
        this.staffRosterVersion += 1;
        this.staffRosterLoading = false;
        this.staffVenueOptionsLoading = false;
        this.staffRosterRequest = null;
        this.staffPagination.loadingAll = false;
    }

    @action
    setStaffRosterCriteria(criteria) {
        this.staffRosterCriteria = { ...emptyRosterFilters(), search: "", ...criteria };
        this.staffRosterVersion += 1;
        this.venueActRoster = [];
        this.groupedVenueActRoster = [];
        this.staffRosterError = "";
        this.staffRosterLoading = false;
        this.resetStaffPaging();
    }

    @action
    resetStaffPaging() {
        this.staffRosterVersion += 1;
        this.staffPageCache = {};
        this.staffRosterRequest = null;
        this.staffRetry = { page: 1, all: false };
        this.staffPagination = { ...this.staffPagination, currentPage: 1, isMore: false, allLoaded: false, loadingAll: false };
    }

    @action
    setStaffRosterVenue(venueId) {
        this.staffRosterVenueId = String(venueId);
        this.setStaffRosterCriteria(this.staffRosterCriteria);
    }

    @action
    setStaffPageSize(size) {
        if (![10, 25, 50, 100].includes(Number(size))) return;
        this.staffPagination.pageSize = Number(size);
        this.setStaffRosterCriteria(this.staffRosterCriteria);
    }

    loadStaffVenueOptions = async actor => {
        if (!this.staffRosterActive || String(profileStore.getCurrentProfile()?.id) !== String(actor)) return;
        if (this.staffVenuesReady && String(this.staffRosterActor) === String(actor)) return;
        if (this.staffVenuesPromise && String(this.staffRosterActor) === String(actor)) return this.staffVenuesPromise;
        if (this.staffVenueOptionsError) throw new Error(this.staffVenueOptionsError);
        const session = this.staffRosterSession;
        const lookupVersion = ++this.staffVenueLookupVersion;
        const isCurrent = () => this.staffRosterActive && this.staffRosterSession === session &&
            lookupVersion === this.staffVenueLookupVersion && String(this.staffRosterActor) === String(actor) &&
            String(profileStore.getCurrentProfile()?.id) === String(actor);
        runInAction(() => { this.staffVenueOptionsLoading = true; });
        // Same no-argument permission reader as My Gigs → Filter Venue.
        // Enrich only current, private copies; never change shared venue-store data.
        this.staffVenuesPromise = agent.Venue.getUserVenuesBasedOnPermissionSimple().then(response => {
            if (!Array.isArray(response) || response.some(venue =>
                !venue || !Number.isSafeInteger(Number(venue.id)) || Number(venue.id) <= 0
            )) throw new Error("Invalid staff venue response.");
            if (isCurrent()) {
                const venues = _.uniqBy(_.cloneDeep(response).map(venue => {
                    venue.id = Number(venue.id);
                    venueStore.mapAdditionalProfileData(venue);
                    return venue;
                }), "id");
                runInAction(() => {
                    this.staffRosterVenues = _.orderBy(venues, venue => venue.profileNameString);
                    this.staffVenuesReady = true;
                });
            }
        }).catch(error => {
            if (isCurrent()) runInAction(() => {
                this.staffVenueOptionsError = "Could not load venue choices. Please retry.";
            });
            throw error;
        }).finally(() => {
            if (this.staffRosterSession === session && lookupVersion === this.staffVenueLookupVersion &&
                String(this.staffRosterActor) === String(actor)) {
                this.staffVenuesPromise = null;
                runInAction(() => { this.staffVenueOptionsLoading = false; });
            }
        });
        return this.staffVenuesPromise;
    };

    @action
    retryStaffVenueOptions = staffId => {
        const actor = profileStore.getCurrentProfile()?.id;
        if (!this.staffRosterActive || !actor || (staffId && String(staffId) !== String(actor))) return Promise.resolve();
        this.staffVenueOptionsError = "";
        return this.loadStaffVenueOptions(actor).catch(() => undefined);
    };

    @action
    loadStaffRoster = (staffId, page = 1, all = false) => {
        const actor = profileStore.getCurrentProfile()?.id;
        const id = staffId || actor;
        if (!this.staffRosterActive || !id || String(id) !== String(actor) ||
            !Number.isSafeInteger(page) || page < 1 || !Number.isSafeInteger(page * this.staffPagination.pageSize)) {
            return Promise.resolve(this.venueActRoster);
        }
        if (String(this.staffRosterActor) !== String(id)) {
            this.staffRosterActor = id;
            this.staffRosterVenues = [];
            this.staffVenuesReady = false;
            this.staffVenuesPromise = null;
            this.staffVenueLookupVersion += 1;
            this.staffVenueOptionsError = "";
            this.venueActRoster = [];
            this.groupedVenueActRoster = [];
            this.resetStaffPaging();
        }
        const requestKey = () => JSON.stringify([this.staffRosterSession, this.staffRosterVersion, id,
            this.staffRosterVenueId, this.staffPagination.pageSize, this.staffRosterCriteria, page, all]);
        if (this.staffRosterRequest?.key === requestKey()) return this.staffRosterRequest.promise;
        const request = {};
        request.promise = this.performStaffRosterLoad(id, page, all).finally(() => {
            if (this.staffRosterRequest === request) this.staffRosterRequest = null;
        });
        // performStaffRosterLoad advances the read generation synchronously.
        // Duplicates join this generation rather than cancelling it with a new read.
        request.key = requestKey();
        this.staffRosterRequest = request;
        return request.promise;
    };

    @action
    async performStaffRosterLoad(actor, page, all) {
        const version = ++this.staffRosterVersion;
        this.staffRetry = { page, all };
        const criteria = { ...this.staffRosterCriteria,
            vibeIds: [...this.staffRosterCriteria.vibeIds], genreIds: [...this.staffRosterCriteria.genreIds] };
        const selectedVenue = this.staffRosterVenueId;
        const limit = this.staffPagination.pageSize;
        const isCurrent = () => this.staffRosterActive && version === this.staffRosterVersion &&
            String(profileStore.getCurrentProfile()?.id) === String(actor);
        this.staffRosterLoading = true;
        this.staffPagination.loadingAll = all;
        this.staffRosterError = "";
        try {
            // Venue choices are independent metadata. Never delay staff-wide
            // data paging or fan it out through individual venues.
            const venueOptions = this.loadStaffVenueOptions(actor).catch(() => false);
            if (selectedVenue !== "all") {
                await venueOptions;
                if (!isCurrent()) return this.venueActRoster;
                if (!this.staffVenuesReady || !this.staffRosterVenues.some(venue => String(venue.id) === selectedVenue)) {
                    throw new Error("Venue is not available.");
                }
            }
            const pages = { ...this.staffPageCache };
            const getPage = async target => {
                if (pages[target]) return pages[target];
                if (!isCurrent()) return null;
                const result = selectedVenue === "all"
                    ? await agent.VenueAct.getStaffActRosterV2(actor, limit, target, criteria)
                    : await agent.VenueAct.getVenueActRosterWithPagination(selectedVenue, limit, target, criteria);
                if (!isCurrent()) return null;
                if (!Array.isArray(result?.data) || typeof result?.isMore !== "boolean" ||
                    (result.isMore && result.data.length === 0)) throw new Error("Invalid paginated roster response.");
                pages[target] = result;
                return result;
            };
            let result;
            if (all) {
                const data = [];
                let next = 1;
                do {
                    result = await getPage(next++);
                    if (!result || !isCurrent()) return this.venueActRoster;
                    data.push(...result.data);
                } while (result.isMore);
                result = { data, isMore: false };
            } else {
                result = await getPage(page);
            }
            if (!result || !isCurrent()) return this.venueActRoster;
            const mapped = new Set();
            for (const row of result.data) {
                for (const profile of [row.act, row.venue, row.act?.myMusician]) {
                    if (!isCurrent()) return this.venueActRoster;
                    if (profile && !mapped.has(profile)) {
                        mapped.add(profile);
                        await this.mapAdditionalProfileData(profile);
                    }
                }
                if (row.act && !row.act.lineupCountName && Number(row.act.lineUpCount) >= 1 && Number(row.act.lineUpCount) <= 99) {
                    row.act.lineupCountName = this.getLineUpCounts(Number(row.act.lineUpCount));
                }
            }
            if (isCurrent()) runInAction(() => {
                this.venueActRoster = result.data;
                this.staffPageCache = pages;
                this.staffPagination.currentPage = all ? 1 : page;
                this.staffPagination.isMore = result.isMore;
                this.staffPagination.allLoaded = all;
                this.groupedVenueActRoster = _.chain(this.venueActRoster).groupBy("venueId").toPairs()
                    .map(pair => _.zipObject(["venueId", "rosters"], pair)).value();
            });
        } catch (error) {
            if (isCurrent()) runInAction(() => {
                this.staffRosterError = "Could not load acts. Please retry.";
            });
        } finally {
            if (isCurrent()) runInAction(() => {
                this.staffRosterLoading = false;
                this.staffPagination.loadingAll = false;
            });
        }
        return this.venueActRoster;
    }

    @action
    cancelStaffLoadAll() {
        this.staffRosterVersion += 1;
        this.staffRosterLoading = false;
        this.staffPagination.loadingAll = false;
    }

    retryStaffRoster = staffId => this.loadStaffRoster(staffId, this.staffRetry.page, this.staffRetry.all);

    @action
    refreshStaffRoster(staffId, refreshVenues = false) {
        const actor = profileStore.getCurrentProfile()?.id;
        if (!this.staffRosterActive || !actor || (staffId && String(staffId) !== String(actor))) {
            return Promise.resolve(this.venueActRoster);
        }
        this.resetStaffPaging();
        if (refreshVenues) {
            this.staffVenuesReady = false;
            this.staffVenuesPromise = null;
            this.staffVenueLookupVersion += 1;
            this.staffVenueOptionsError = "";
        }
        return this.loadStaffRoster(staffId);
    }
    @observable pendingRequest = [];
    @observable venueActRoster = [];

    @observable groupedPendingRequest = [];
    @observable groupedVenueActRoster = [];

    @observable invitedAct = undefined;
    @observable invitationMessage = "";

    @observable invitedActs = [];
    @observable invitedActsVenues = [];

    @observable venueId = undefined;

    @observable destinationProfile = undefined;
    @observable invitedChecked = [];
    @observable isMoreFavouriteAct = false
    @observable venueActFavouritePage = 1
    @action
    setInvitedChecked(acts) {
        this.invitedChecked = acts;
    }

    @action
    setActiveVenueId(venueId) {
        this.venueId = venueId;
    }

    @action
    setDestinationProfile(profile) {
        this.destinationProfile = profile;
    }

    @action
    resetPage() {
        this.venueActFavouritePage = 1;
        this.isMoreFavouriteAct = false;
        this.venueActRoster = [];
    }
    @action
    resetList() {
        this.staffRosterVersion += 1;
        this.pendingRequest = undefined;
        this.venueActRoster = undefined;

        this.pendingRequest = [];
        this.venueActRoster = [];
        this.groupedPendingRequest = [];
        this.groupedVenueActRoster = [];
    }

    @action
    getVenueActRosterPending = venueId => {
        this.pendingRequest = [];
        agent.ProfileRelationship.getVenueActRoster(venueId).then(
            action(response => {
                if (response.length > 0) {
                    this.pendingRequest = response.map(data => {
                        profileStore.mapAdditionalProfileData(data.primaryProfile);
                        profileStore.mapAdditionalProfileData(data.secondaryProfile);
    
                        if (data.primaryProfile.type === ProfileType.Act.ordinal && data.primaryProfile.myMusician) {
                            profileStore.mapAdditionalProfileData(data.primaryProfile.myMusician);
                        } else if (data.secondaryProfile.type === ProfileType.Act.ordinal && data.secondaryProfile.myMusician) {
                            profileStore.mapAdditionalProfileData(data.secondaryProfile.myMusician);
                        }
                        return data;
                    });
                }
            })
        );
    };
    @action
    getVenueActRosterSimple = venueId => { 
        this.venueActRoster = [];
        return agent.VenueAct.getVenueActRosterSimpleData(venueId).then(
            action(async response => {
                let result = [];
                if (response.length) {
                    // profileStore.mapAdditionalProfileNames(data);
                    response.forEach(data => {
                        profileStore.mapAdditionalProfileNames(data.act);
                        profileStore.mapAdditionalProfileNames(data.venue);
                        if (data.act.myMusician) {
                           profileStore.mapAdditionalProfileNames(data.act.myMusician) 
                        }
                        result.push(data);
                        this.venueActRoster.push(data)
                    });
                    this.venueActRoster = _.orderBy(this.venueActRoster, x=>x.act.actNameString)
                } else {
                    this.venueActRoster = [];
                }
                return result;
            })
        );
    }
    @action
    getVenueActRoster = venueId => {
        this.venueActRoster = [];
        return agent.VenueAct.getVenueActRoster(venueId).then(
            action(async response => {
                let result = [];
                if (response.length) {
                    await Promise.all(response.map(async data => {
                        await this.mapAdditionalProfileData(data.act);
                        await this.mapAdditionalProfileData(data.venue);
                        if (data.act.myMusician) {
                            await this.mapAdditionalProfileData(data.act.myMusician);
                        }
                        runInAction(() => {
                            result.push(data);
                            this.venueActRoster.push(data)
                        }); // use to async/await and push mapadditionaldata in mobx 
                    }))
                    runInAction(() => {
                        this.venueActRoster = _.orderBy(this.venueActRoster, x=>x.act.actNameString)
                    })
                    // for(var i = 0; i < response.length;i++){
                    //     var data = response[i]
                    //     await this.mapAdditionalProfileData(data.act);
                    //     await this.mapAdditionalProfileData(data.venue);
                    //     if (data.act.myMusician) {
                    //         await this.mapAdditionalProfileData(data.act.myMusician);
                    //     }
                    //     runInAction(() => {
                    //         result.push(data);
                    //         this.venueActRoster.push(data)
                    //     }); // use to async/await and push mapadditionaldata in mobx 
                    // }
                } else {
                    this.venueActRoster = [];
                }
                return result;
            })
        );
    };

    @action
    getVenueActRosterWithPagination = (venueId,limit,page) => {
        return agent.VenueAct.getVenueActRosterWithPagination(venueId, limit, page).then(
            action(async res => {
                var response = res.data
                let result = [];
                if (response.length) {
                    await Promise.all(response.map(async data => {
                        await this.mapAdditionalProfileData(data.act);
                        await this.mapAdditionalProfileData(data.venue);
                        if (data.act.myMusician) {
                            await this.mapAdditionalProfileData(data.act.myMusician);
                        }
                        runInAction(() => {
                            result.push(data);
                            this.venueActRoster.push(data)
                            this.isMoreFavouriteAct = res.isMore
                        }); // use to async/await and push mapadditionaldata in mobx 
                    }))
                    // for(var i = 0; i < response.length;i++){
                    //     var data = response[i]
                    //     await this.mapAdditionalProfileData(data.act);
                    //     await this.mapAdditionalProfileData(data.venue);
                    //     if (data.act.myMusician) {
                    //         await this.mapAdditionalProfileData(data.act.myMusician);
                    //     }
                    //     runInAction(() => {
                    //         result.push(data);
                    //         this.venueActRoster.push(data)
                    //     }); // use to async/await and push mapadditionaldata in mobx 
                    // }
                } else {
                    // this.venueActRoster = [];
                }
                return result;
            })
        );
    };

    
    @action
    getStaffPendingActRoster = staffId => {
        const context = this.captureStaffMutation();
        if (context && staffId && String(staffId) !== String(context.actor)) return Promise.resolve(this.pendingRequest);
        const version = ++this.staffPendingRosterVersion;
        const { profile } = profileStore;
        if (!staffId) {
            staffId = context?.actor || profile.id;
        }
        this.pendingRequest = [];
        return agent.ProfileRelationship.getStaffActRoster(staffId).then(
            action(response => {
                if (version !== this.staffPendingRosterVersion || !this.isStaffMutationCurrent(context)) return this.pendingRequest;
                if (!response || response.length === 0) return this.pendingRequest = [];
                this.pendingRequest = response.map(async data => {
                    await this.mapAdditionalProfileData(data.primaryProfile);
                    profileStore.mapAdditionalProfileData(data.secondaryProfile);

                    if (data.primaryProfile.type === ProfileType.Act.ordinal && data.primaryProfile.myMusician) {
                        profileStore.mapAdditionalProfileData(data.primaryProfile.myMusician);
                    } else if (data.secondaryProfile.type === ProfileType.Act.ordinal && data.secondaryProfile.myMusician) {
                        profileStore.mapAdditionalProfileData(data.secondaryProfile.myMusician);
                    }
                    return data;
                });

                this.groupedPendingRequest = _.chain(this.pendingRequest)
                    .groupBy("primaryProfileId")
                    .toPairs()
                    .map(function(pair) {
                        return _.zipObject(["venueId", "requests"], pair);
                    })
                    .value();
                return this.pendingRequest;
            })
        );
    };

    @action
    getStaffActRoster = (staffId, isUnique = false) => {
        if (this.staffRosterActive && !isUnique) {
            return this.refreshStaffRoster(staffId, true);
        }
        const version = ++this.staffRosterVersion;
        const actor = profileStore.getCurrentProfile()?.id;
        const isCurrent = () => version === this.staffRosterVersion &&
            String(profileStore.getCurrentProfile()?.id) === String(actor);
        const { profile } = profileStore;
        if (!staffId && profile) {
            staffId = profile.id;
        } 
        this.venueActRoster = [];
        const requestVenueActRoster = isUnique ? agent.VenueAct.getStaffActRosterUnique : agent.VenueAct.getStaffActRoster;
        return requestVenueActRoster(staffId).then(
            action(async response => {
                if (!isCurrent()) return this.venueActRoster;
                if (!response) return this.venueActRoster = [];
                this.venueActRoster = []
                var actIds = [];
                var venueIds = []
                var myMusicianIds = []
                var actData = {}
                var venueData = {}
                var myMusicianData = {}
                await Promise.all(response.map(async data => {  
                    if(data.act && data.act.id && actIds.filter(x=>x == data.act.id).length == 0){
                        actIds.push(data.act.id)
                        await this.mapAdditionalProfileData(data.act);
                        actData[data.act.id] = data.act
                    }  
                    if(data.venue && data.venue.id && venueIds.filter(x=>x == data.venue.id).length == 0){
                        venueIds.push(data.venue.id)
                        await this.mapAdditionalProfileData(data.venue);
                        venueData[data.venue.id] = data.venue
                    }  
                    
                   
                    
                    if (data.act && data.act.myMusician) {
                        if(data.act.myMusician.id && myMusicianIds.filter(x=>x == data.act.myMusician.id).length == 0){
                            myMusicianIds.push(data.act.myMusician.id)
                            await this.mapAdditionalProfileData(data.act.myMusician);
                            myMusicianData[data.act.myMusician.id] = data.act.myMusician
                        }  
                        
                    }
                    
                }));
                var venueActData = [];
                response.forEach(data => {
                    if(data.act && data.act.id && data.venue && data.venue.id){
                        if(data.act && data.act.id){
                            data.act = actData[data.act.id]
                        }else{
                            deletedData.push(data.id)
                        }
                        if(data.venue && data.venue.id){
                            data.venue = venueData[data.venue.id]
                        }else{
                            deletedData.push(data.id)
                        }
                        if (data.act && data.act.myMusician) {
                            data.act.myMusician = myMusicianData[data.act.myMusician.id]
                        }
                        venueActData.push(data);
                    }
                    
                       
                });
                
                if (!isCurrent()) return this.venueActRoster;
                runInAction(() => {
                    this.venueActRoster = _.orderBy(venueActData, x => x.act.actNameSort, ['asc']);
                    this.groupedVenueActRoster = _.chain(this.venueActRoster)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function(pair) {
                        return _.zipObject(["venueId", "rosters"], pair);
                    })
                    .value()
                })
            })
        );
    };

    @action
    updateVenueAct(venueActId, isSwitchOn){
        var data = {
            invoiceType: isSwitchOn ? 1: 0
        }
        return agent.VenueAct.updateVenueAct(venueActId,data)
        .then(
            action(async result => {
                if(result){
                    var venueAct = this.venueActRoster.filter(x=>x.id === venueActId)
                    if(venueAct && venueAct.length){
                        venueAct[0].invoiceType = data.invoiceType
                    }
                    this.groupedVenueActRoster = _.chain(this.venueActRoster)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function(pair) {
                        return _.zipObject(["venueId", "rosters"], pair);
                    })
                    .value()
                }
                
            })
        )
    }

        @action
    getLineUpCounts(instrument) {
        if (!instrument || instrument === 0) {
            return "";
        }

        let lineUpCounts = [
            { name: "Solo", instrument: 1 },
            { name: "Duo", instrument: 2 },
            { name: "Trio", instrument: 3 },
            { name: "Quartet / 4 Piece", instrument: 4 },
            { name: "Quintet / 5 Piece", instrument: 5 },
            { name: "Sextet / 6 Piece", instrument: 6 }
        ];

        for (var i = 0; i < 93; i++) {
            lineUpCounts.push({ name: i + 7 + " Piece", instrument: i + 7 });
        }

        return lineUpCounts.filter(lineUpCount => lineUpCount.instrument === instrument)[0].name;
    }

    @action
    updateVenueActInvoiceTypeFromAct(venueActs){
        var data = []
        venueActs.forEach(venueAct => {
            var item = {
                venueActId: venueAct.id,
                invoiceType: venueAct.act.invoiceType
            }
            data.push(item)
        });

            return agent.VenueAct.updateVenueActFromAct(data)
            .then(
                action(async result => {
                    if(result){
                        data.forEach(item => {
                            var selectedResult = result.find(x => x.venueActId == item.venueActId);
                            if(selectedResult){
                                item.isSuccess = selectedResult.isSuccess
                            }
                            
                        });
                        this.venueActRoster.forEach(venueAct => {
                            var selectedData = data.find(x => x.venueActId == venueAct.id);
                            if(selectedData && selectedData.isSuccess){
                                venueAct.invoiceType = selectedData.invoiceType
                            }
                        });
                            
                    }
                    return result
                    
                })
            )
            .finally(
                action(() => {
                    this.isLoadingAcceptedActs = false;
                })
            );
    }
    
    @action
    getStaffActRosterInvoice = (staffId, isUnique = false) => {
        const { profile } = profileStore;
        if (!staffId && profile) {
            staffId = profile.id;
        } 
        this.venueActRoster = [];
        const requestVenueActRoster = isUnique ? agent.VenueAct.getStaffActRosterUnique : agent.VenueAct.getStaffActRosterSimple;
        return requestVenueActRoster(staffId).then(
            action(async response => {
                if (!response) return this.venueActRoster = [];
                this.venueActRoster = []
                var mapped = []

                await Promise.all(response.map(async data => {      
                    await this.mapAdditionalProfileData(data.act);
                    await this.mapAdditionalProfileData(data.venue);
                    if (data.act.myMusician) {
                        await this.mapAdditionalProfileData(data.act.myMusician);
                    }
                    mapped.push(data);
                }));
                runInAction(() => {
                    this.venueActRoster = _.orderBy(mapped, x=>x.act.actNameString)
                    this.groupedVenueActRoster = _.chain(this.venueActRoster)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function(pair) {
                        return _.zipObject(["venueId", "rosters"], pair);
                    })
                    .value()
                    
                })
                return this.venueActRoster
            })
        );
    };

    @action
    getStaffActRosterInvoiceWithAdmin = (staffId, isUnique = false) => {
        const { profile } = profileStore;
        if (!staffId && profile) {
            staffId = profile.id;
        } 
        this.venueActRoster = [];
        const requestVenueActRoster = isUnique ? agent.VenueAct.getStaffActRosterUniqueWithAdmin : agent.VenueAct.getStaffActRosterSimple;
        return requestVenueActRoster(staffId).then(
            action(async response => {
                if (!response) return this.venueActRoster = [];
                this.venueActRoster = []
                var mapped = []

                await Promise.all(response.map(async data => {      
                    await this.mapAdditionalProfileData(data.act);
                    await this.mapAdditionalProfileData(data.venue);
                    if (data.act.myMusician) {
                        await this.mapAdditionalProfileData(data.act.myMusician);
                    }
                    mapped.push(data);
                }));
                runInAction(() => {
                    this.venueActRoster = _.orderBy(mapped, x=>x.act.actNameString)
                    this.groupedVenueActRoster = _.chain(this.venueActRoster)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function(pair) {
                        return _.zipObject(["venueId", "rosters"], pair);
                    })
                    .value()
                    
                })
                return this.venueActRoster
            })
        );
    };

    @action
    getStaffActRosterMyGigs = (staffId, isUnique = false) => {
        const { profile } = profileStore;
        if (!staffId && profile) {
            staffId = profile.id;
        } 
        this.venueActRoster = [];
        const requestVenueActRoster = isUnique ? agent.VenueAct.getStaffActRosterUnique : agent.VenueAct.getStaffActRosterSimple;
        return requestVenueActRoster(staffId).then(
            action(async response => {
                if (!response) return this.venueActRoster = [];
                let result = []
                for (const data of response) {     
                    await this.mapAdditionalProfileData(data.act);
                    await this.mapAdditionalProfileData(data.venue);
                    if (data.act.myMusician) {
                        await this.mapAdditionalProfileData(data.act.myMusician);
                    }
                    result.push(data);
                }
                runInAction(() => {
                    this.venueActRoster = _.orderBy(result, x=>x.act.actNameString)
                    this.groupedVenueActRoster = _.chain(this.venueActRoster)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function(pair) {
                        return _.zipObject(["venueId", "rosters"], pair);
                    })
                    .value()
                })
                return result
            
            })
        );
    };

    @action
    getStaffAvailableActs = (bookingDetail, staffId) => {
        const { currentProfile } = profileStore;

        if (!staffId) {
            staffId = currentProfile.id;
        }

        return agent.VenueAct.getStaffAvailableActs(staffId, bookingDetail).then(
            action(async response => {
                var result = [];
                for(var i = 0; i< response.length; i++){
                    var data = response[i];
                    await profileStore.mapAdditionalProfileDataAsync(data.act);
                    await profileStore.mapAdditionalProfileDataAsync(data.venue);
                    // profileStore.mapAdditionalProfileData(data.act);
                    // profileStore.mapAdditionalProfileData(data.venue);
                    if (data.act.myMusician) {
                        await profileStore.mapAdditionalProfileDataAsync(data.act.myMusician);
                        // profileStore.mapAdditionalProfileData(data.act.myMusician);
                    }
                    result.push(data);
                }
                this.venueActRoster = _.orderBy(result, x=>x.act.actNameString)
                return this.venueActRoster;
            })
        );
    };

    @action
    getMusicianVenueActRosterPending = () => {
        this.pendingRequest = [];
        return agent.ProfileRelationship.getMusicianActVenueRoster().then(
            action(response => {
                this.pendingRequest = response.map(data => {
                    profileStore.mapAdditionalProfileData(data.primaryProfile);
                    profileStore.mapAdditionalProfileData(data.secondaryProfile);

                    if (data.primaryProfile.type === ProfileType.Act.ordinal && data.primaryProfile.myMusician) {
                        profileStore.mapAdditionalProfileData(data.primaryProfile.myMusician);
                    } else if (data.secondaryProfile.type === ProfileType.Act.ordinal && data.secondaryProfile.myMusician) {
                        profileStore.mapAdditionalProfileData(data.secondaryProfile.myMusician);
                    }
                    return data;
                });

                this.pendingRequest = _.chain(this.pendingRequest)
                    .groupBy("primaryProfileId")
                    .toPairs()
                    .map(function(pair) {
                        return _.zipObject(["venue", "request"], pair);
                    })
                    .value();
                return this.pendingRequest;
            })
        );
    };

    @action
    getMusicianVenueActRoster = () => {
        this.venueActRoster = [];
        const profile = profileStore.getCurrentProfile();
        return agent.VenueAct.getMusicianActVenue(profile.id).then(
            action(async response => {
                if(response && response.length > 0 && response !== undefined) {
                    var mapped = [];
                    for(var i = 0; i < response.length; i++){
                        var data = response[i]
                        await profileStore.mapAdditionalProfileDataAsync(data.venue);
                        await profileStore.mapAdditionalProfileDataAsync(data.act);
                        data.act.invoiceType = data.invoiceType

                        mapped.push(data)
                    }
                    runInAction(() => {
                        this.venueActRoster = _.orderBy(mapped, x=>x.act.actNameString)
                        this.venueActRoster = _.chain(this.venueActRoster)
                        .groupBy("venueId")
                        .toPairs()
                        .map(function(pair) {
                            return _.zipObject(["venue", "data"], pair);
                        })
                        .value();
                        return this.venueActRoster;
                     })
                } else {
                    this.venueActRoster = []
                    return mapped;
                }
            })
        );
    };

    
    @action
    deleteVenueActMusician(id) {

        return agent.VenueAct.deleteMusician(id).then(
            action(response => {
                var result = this.venueActRoster
                result.forEach(item => {
                    item.data = item.data.filter(x=>parseInt(x.id) != parseInt(id))
                });
                result = result.filter(x=>x.data.length)
                this.venueActRoster = result
                this.groupedVenueActRoster = _.chain(this.venueActRoster)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function(pair) {
                        return _.zipObject(["venueId", "rosters"], pair);
                    })
                    .value();
                profileStore.mapAdditionalProfileData(response.act);
                profileStore.mapAdditionalProfileData(response.venue);

                return response;
            })
        );
    }
    @action
    acceptInvitation(invitation) {
        const { profile } = profileStore;
        const requestDeatail = {
            primaryProfileId: Number(invitation.invitedVenue),
            secondaryProfileId: Number(invitation.additionalData),
            requestMessage: invitation.invitationMessage,
            type: ProfileRelationType.VenueAct.ordinal,
            createdById: profile.id ,
            invitationId: invitation.id
        }
        return agent.ProfileRelationship.invite(requestDeatail).then(
            action(response => {
            })
        )
    }

    @action
    inviteMany(checkedOnly) {
        const { profile } = profileStore;
        let type
        if (this.destinationProfile && this.destinationProfile.type === ProfileType.Agent.ordinal){
            type = ProfileRelationType.AgentAct.ordinal
        } else {
            type = ProfileRelationType.VenueAct.ordinal
        }

        if (this.invitedActs && this.invitedActs.length > 0) {
            var requestDetails = [];
            var listActs = checkedOnly ? this.invitedChecked : this.invitedActs;
            listActs.forEach(invitedAct => {
                requestDetails.push({
                    primaryProfileId: this.venueId,
                    secondaryProfileId: invitedAct.id,
                    requestMessage: this.invitationMessage,
                    type: type,
                    createdById: profile.id
                });
            });

            return agent.ProfileRelationship.inviteMany(requestDetails).then(
                action(response => {
                    this.getVenueActRoster(this.venueId);
                    this.getVenueActRosterPending(this.venueId);
                })
            );
        }

        return Promise.resolve();
    }

    @action
    inviteManyActToManyVenues() {
        const profile = profileStore.getCurrentProfile();
        const profileId = profile?.id;
        if (!["number", "string"].includes(typeof profileId)
            || (typeof profileId === "string" && !/^\d+$/.test(profileId))
            || !Number.isSafeInteger(Number(profileId)) || Number(profileId) <= 0) {
            return Promise.reject(new Error("Select a valid active profile before inviting an act."));
        }

        if (this.invitedActs && this.invitedActs.length > 0) {
            var requestDetails = [];

            this.invitedActs.forEach(invitedAct => {
                if(this.invitedActsVenues.length === 0) {
                    requestDetails.push({
                        primaryProfileId: profileId,
                        secondaryProfileId: invitedAct.id,
                        requestMessage: this.invitationMessage,
                        type: ProfileRelationType.AgentAct.ordinal,
                        createdById: profileId,
                        profileId
                    });
                } else {
                    this.invitedActsVenues.forEach(invitedActVenue => {
                        requestDetails.push({
                            primaryProfileId: invitedActVenue.id,
                            secondaryProfileId: invitedAct.id,
                            requestMessage: this.invitationMessage,
                            type: ProfileRelationType.VenueAct.ordinal,
                            createdById: profileId,
                            profileId
                        });
                    });
                }
            });

            return agent.ProfileRelationship.inviteMany(requestDetails).then(
                action(() => {
                    if (String(profileStore.getCurrentProfile()?.id) !== String(profileId)) return;
                    return this.getStaffPendingActRoster(profileId).then(() => {
                        if (String(profileStore.getCurrentProfile()?.id) !== String(profileId)) return;
                        return this.getStaffActRoster(profileId).then(() => {
                            return this.pendingRequest;
                        });
                    });
                })
            );
        }

        return Promise.resolve();
    }

    @action
    setvenueActFavouritePage(page){
        this.venueActFavouritePage = page
    }
    @action
    addMany(venue) {
        const { profile } = profileStore;

        if (this.invitedActs && this.invitedActs.length > 0 && venue) {
            var requestDetails = [];

            this.invitedActs.forEach(invitedAct => {
                requestDetails.push({
                    primaryProfileId: venue.id,
                    primaryProfile: {type: venue.type},
                    secondaryProfileId: invitedAct.id,
                    secondaryProfile: {type: invitedAct.type},
                    type: ProfileRelationType.VenueAct.ordinal,
                    createdById: profile.id
                });
            });

            return agent.VenueAct.createMany(requestDetails).then(
                action(response => {
                    return response;
                })
            );
        }else if(this.invitedAct){
            var requestDetails = [];
                requestDetails.push({
                    primaryProfileId: venue.id,
                    primaryProfile: {type: venue.type},
                    secondaryProfileId: this.invitedAct.id,
                    secondaryProfile: {type: this.invitedAct.type},
                    type: ProfileRelationType.VenueAct.ordinal,
                    createdById: profile.id
                });
            return agent.VenueAct.createMany(requestDetails).then(
                action(response => {
                    return response;
                })
            );
        }

        return Promise.resolve();
    }

    @action
    setInvitedActs(acts) {
        this.invitedActs = acts;
    }

    @action
    setInvitedActsVenues(venues) {
        this.invitedActsVenues = venues;
    }

    @action
    setInvitationMessage(value) {
        this.invitationMessage = value;
    }

    @action
    setInvitedAct(act) {
        profileStore.mapAdditionalProfileData(act.myMusician);

        var checkPending = this.pendingRequest.filter(
            x => x.secondaryProfileId === act.id && this.invitedActsVenues.filter(y => y.id === x.primaryProfileId).length > 0
        );

        var checkAccepted = this.venueActRoster.filter(x => x.actId === act.id && this.invitedActsVenues.filter(y => y.id === x.venueId).length > 0);

        if (checkPending.length > 0) {
            checkPending.forEach(data => {
                this.invitedActsVenues = this.invitedActsVenues.filter(x => x.id !== data.primaryProfileId);
            });
        }

        if (checkAccepted.length > 0) {
            checkAccepted.forEach(data => {
                this.invitedActsVenues = this.invitedActsVenues.filter(x => x.id !== data.venueId);
            });
        }

        if (act.myMusician && act.myMusician.myActs) {
            act.myMusician.myActs = act.myMusician.myActs.map(data => {
                profileStore.mapAdditionalProfileData(data);
                return data;
            });
            this.invitedAct = act;
            if (!this.invitedActs) {
                this.invitedActs = [];
            }
            this.invitedActs.push(act);
        }
        else{
            this.invitedAct = [];
            return agent.Profile.loadActs(act.userId)
                .then(
                    action(async musician => {
                        await profileStore.mapAdditionalProfileDataAsync(musician.myMusician);
                        if(musician.myActs.length > 0 && Array.isArray(musician.myActs)){
                            for(let i = 0; i < musician.myActs.length; i++) {
                                await profileStore.mapAdditionalProfileDataAsync(musician.myActs[i])
                                musician.myActs[i].selectedActId = act.id
                            }
                        }
                        act.myMusician = musician.myMusician
                        act.myActs = musician.myActs
                        runInAction(() => {
                            this.invitedAct = act;
                   
                            return this.invitedAct
                          
                        })
                    })
                );
        }
    }

    @action
    clearInviteData() {
        this.invitationMessage = "";
        this.invitedAct = undefined;
        this.invitedActs = [];
        this.invitedActsVenues = [];
    }

    //end of invitation

    @action
    acceptRequest(request) {
        return agent.ProfileRelationship.accept(request).then(
            action(response => {
                //TODO: delete and add based on response
                this.getMusicianVenueActRosterPending();
                this.getMusicianVenueActRoster();
                return response;
            })
        );
    }

    @action
    acceptManyRequest(requests) {
        var data = [];
        requests.forEach(request => {
            data.push(request);
        });
        return agent.ProfileRelationship.acceptMany(data).then(
            action(response => {
                //TODO: delete and add based on response
                this.getMusicianVenueActRosterPending();
                this.getMusicianVenueActRoster();
                return response;
            })
        );
    }

    @action
    removeRequest(request) {
        return agent.ProfileRelationship.remove(request).then(
            action(response => {
                this.pendingRequest = this.pendingRequest.filter(x => x.id !== response.id);
                this.groupedPendingRequest = _.chain(this.pendingRequest)
                    .groupBy("primaryProfileId")
                    .toPairs()
                    .map(function(pair) {
                        return _.zipObject(["venueId", "requests"], pair);
                    })
                    .value();
                profileStore.mapAdditionalProfileData(response.primaryProfile);
                profileStore.mapAdditionalProfileData(response.secondaryProfile);
                return response;
            })
        );
    }

    @action
    removeManyRequest(requests) {
        var data = [];
        requests.forEach(request => {
            data.push(request.id);
        });
        if (data.length > 0) {
            return agent.ProfileRelationship.removeMany(data).then(
                action(response => {
                    response.forEach(data => {
                        this.pendingRequest = this.pendingRequest.filter(x => x.id !== data.id);
                    });
                    this.groupedPendingRequest = _.chain(this.pendingRequest)
                        .groupBy("primaryProfileId")
                        .toPairs()
                        .map(function(pair) {
                            return _.zipObject(["venueId", "requests"], pair);
                        })
                        .value();
                    var result = response[0];
                    profileStore.mapAdditionalProfileData(result.primaryProfile);
                    profileStore.mapAdditionalProfileData(result.secondaryProfile);
                    return result;
                })
            );
        } else {
            return Promise.resolve(data);
        }
    }

    @action
    declineRequest(request) {
        return agent.ProfileRelationship.decline(request).then(
            action(response => {
                _.remove(this.pendingRequest, ["id", response.id]);
                return response;
            })
        );
    }

    @action
    declineManyRequest(requests) {
        var data = [];
        requests.forEach(request => {
            data.push(request.id);
        });
        return agent.ProfileRelationship.declineMany(data).then(
            action(response => {
                response.forEach(data => {
                    _.remove(this.pendingRequest, ["id", data.id]);
                });
                return response;
            })
        );
    }

    @action
    deleteVenueAct(request, cancelBookings) {
        const context = this.captureStaffMutation();
        return agent.VenueAct.delete(request, cancelBookings).then(
            action(response => {
                if (!this.isStaffMutationCurrent(context)) return response;
                this.venueActRoster = this.venueActRoster.filter(x => x.id !== response.id);
                this.groupedVenueActRoster = _.chain(this.venueActRoster)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function(pair) {
                        return _.zipObject(["venueId", "rosters"], pair);
                    })
                    .value();
                profileStore.mapAdditionalProfileData(response.act);
                profileStore.mapAdditionalProfileData(response.venue);

                return context ? this.refreshStaffRoster(context.actor).then(() => response) : response;
            })
        );
    }

    @action
    deleteManyVenueAct(requests) {
        const context = this.captureStaffMutation();
        var data = [];
        requests.forEach(request => {
            data.push(request.id);
        });
        if (data.length > 0) {
            return agent.VenueAct.deleteMany(data).then(
                action(response => {
                    if (!this.isStaffMutationCurrent(context)) return response[0];
                    response.forEach(data => {
                        this.venueActRoster = this.venueActRoster.filter(x => x.id !== data.id);
                    });
                    this.groupedVenueActRoster = _.chain(this.venueActRoster)
                        .groupBy("venueId")
                        .toPairs()
                        .map(function(pair) {
                            return _.zipObject(["venueId", "rosters"], pair);
                        })
                        .value();
                    var result = response[0];
                    profileStore.mapAdditionalProfileData(result.act);
                    profileStore.mapAdditionalProfileData(result.venue);
                    return context ? this.refreshStaffRoster(context.actor).then(() => result) : result;
                })
            );
        } else {
            return Promise.resolve(data);
        }
    }

    @action
    captureStaffMutation = () => this.staffRosterActive ? {
        actor: profileStore.getCurrentProfile()?.id, session: this.staffRosterSession
    } : null;

    isStaffMutationCurrent = context => context
        ? this.staffRosterActive && context.session === this.staffRosterSession &&
            String(profileStore.getCurrentProfile()?.id) === String(context.actor)
        : !this.staffRosterActive;

    mapAdditionalProfileData = async (data) => {
        await profileStore.mapAdditionalProfileImageAndBackgroundImages(data);
        profileStore.mapAdditionalProfileNames(data);
    }
}

export default new VenueActStore();
